import { useRouter } from "next/router";
import { useMemo } from "react";
import { ExecutePianoProps } from "~/components/Piano/useExecutePiano-types";
import {
  isGiftTargetRoute,
  hasGiftParams,
  GiftQueryParams,
} from "~/lib/constants/gift-routes";
import { useGiftValidation } from "~/lib/client/useGiftValidation";

/**
 * Hook to handle Piano props logic, using gift validation internally.
 */
export const useGiftContentAccess = (
  baseProps: ExecutePianoProps,
  pageProps?: { postId?: number }
): {
  finalProps: ExecutePianoProps;
  isValidationComplete: boolean;
} => {
  const { query, pathname, isReady } = useRouter();
  const tokenParam = query.t as string | undefined;
  const hashParam = query.h as string | undefined;
  const giftParams: GiftQueryParams = useMemo(
    () => ({ t: tokenParam, h: hashParam }),
    [tokenParam, hashParam]
  );
  const shouldValidate = useMemo(() => {
    if (!isReady) return false;
    return isGiftTargetRoute(pathname) && hasGiftParams(giftParams);
  }, [isReady, pathname, giftParams]);

  const { isGiftValid, isGiftExpired, isValidationComplete } =
    useGiftValidation(pageProps?.postId, shouldValidate);

  const finalProps = useMemo(() => {
    if (shouldValidate && baseProps.type === "detail") {
      const existingTags = baseProps.tags || [];

      const filteredTags = existingTags.filter(
        (tag) => !(tag.prefix === "paywall")
      );

      if (isGiftValid) {
        return {
          ...baseProps,
          tags: [...filteredTags, { slug: "gift", prefix: "paywall" }],
        };
      }

      if (isGiftExpired) {
        return {
          ...baseProps,
          tags: [...filteredTags, { slug: "giftexpired", prefix: "paywall" }],
        };
      }
    }
    return baseProps;
  }, [baseProps, isGiftValid, isGiftExpired, shouldValidate]);

  return {
    finalProps,
    isValidationComplete,
  };
};
